import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from '@betty/i18n';
import { Box, Text } from '@betty/moonlight';
import { useSelector } from '@betty/toolkit/state';
import {
  getMilestoneFlipReward,
  getMilestoneRewards,
} from '../../utils/jackpot';
import { getOtherPrizes } from './CommunityJackpotRewards.utils';
import { CommunityJackpotRewardsProps } from './CommunityJackpotRewards.types';
import { selectCommunityJackpotMilestone } from '../../state';
import CommunityJackpotRewardCard from './CommunityJackpotRewardCard/CommunityJackpotRewardCard';
import CommunityJackpotTable from '../CommunityJackpot/CommunityJackpotTable/CommunityJackpotTable';

function CommunityJackpotRewards({
  milestoneId,
}: CommunityJackpotRewardsProps) {
  const { t } = useTranslation('communityJackpot');
  const milestone = useSelector((state) =>
    selectCommunityJackpotMilestone(state, milestoneId),
  );
  const [rewardsLength, flipReward, otherPrizes] = useMemo(
    () => [
      getMilestoneRewards(milestone?.prizes || []),
      getMilestoneFlipReward(milestone),
      getOtherPrizes(milestone?.prizes || []),
    ],
    [milestone],
  );

  const renderHeader = useCallback(
    () => (
      <Box>
        <Text
          variant="h4"
          locKey="rewardsInfoTitle"
          locNS="communityJackpot"
          color="darkSurfacePrimary"
          paddingBottom="space-200"
        />
        <Text variant="body-m" color="darkSurfacePrimary">
          {t('rewardsInfoDescription', {
            count: rewardsLength + 1, // plus jackpot award
          })}
        </Text>
        <Box gap="space-800" paddingTop="space-800" paddingBottom="space-1200">
          <CommunityJackpotRewardCard
            label={milestone?.label || t('rewardsInfoCardLabel')}
            amount={milestone?.rewardAmount || 0}
          />
          {flipReward && (
            <CommunityJackpotRewardCard
              label={t('rewardsInfoCardFlipRewardLabel')}
              info={t('milestoneMakerBonusInfo')}
              amount={flipReward.rewardAmount}
            />
          )}
        </Box>
      </Box>
    ),
    [flipReward, milestone?.label, milestone?.rewardAmount, rewardsLength, t],
  );

  const renderFooter = useCallback(
    () => (
      <Text
        variant="caption"
        locKey="rewardsInfoCaption"
        locNS="communityJackpot"
        color="darkSurfaceMuted"
        paddingTop="space-1200"
        textAlign="center"
      />
    ),
    [],
  );

  return (
    <CommunityJackpotTable
      variant="rewards"
      prizes={otherPrizes}
      renderHeader={renderHeader}
      renderFooter={renderFooter}
    />
  );
}

export default memo(CommunityJackpotRewards);
